import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import RecentResults from "@/components/RecentResults";
import Announcements from "@/components/Announcements";
import GlobalLeaderboards from "@/components/GlobalLeaderboards";
import FootballNews from "@/components/FootballNews";
import MarketNews from "@/components/MarketNews";
import TrophyNews from "@/components/TrophyNews";
import FeaturedLinks from "@/components/FeaturedLinks";

const DIVISION_IMAGES = {
    TOP: "https://static.prod-images.emergentagent.com/jobs/10d0f246-91a4-44c6-8e93-6d06f15a1a85/images/69e94978c93771bccdb4b3886124e92b0c75c486d298b1a41fc17e92e252db37.png",
    DOP: "https://static.prod-images.emergentagent.com/jobs/10d0f246-91a4-44c6-8e93-6d06f15a1a85/images/267648002b07fb240bb7e365b5a9bf5bacea4011b39c277bbffea73cf5797a61.png",
    OPS: "https://static.prod-images.emergentagent.com/jobs/10d0f246-91a4-44c6-8e93-6d06f15a1a85/images/0b0ca797956d1cc842047fc0d7d99c7504af6e3dfaf8a6dc7353bce101bbde7e.png",
    HOPE: "https://static.prod-images.emergentagent.com/jobs/10d0f246-91a4-44c6-8e93-6d06f15a1a85/images/5953418f9f286f6d3d2fd2f37de679ad22b6f90c61ca2e70137fe7a2d3255eef.png",
    PRIMAVERA: "/divisions/primavera.webp",
};

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/10d0f246-91a4-44c6-8e93-6d06f15a1a85/images/4a8f907e09f016275d01a077c3ff6241efc2159287233bc186dc8da5294e3248.png";

export default function Landing() {
    const [divisions, setDivisions] = useState([]);
    const [stats, setStats] = useState({ teams: 0, articles: 0 });

    useEffect(() => {
        (async () => {
            try {
                const [d, t, a] = await Promise.all([
                    api.get("/divisions"),
                    api.get("/teams"),
                    api.get("/regulation"),
                ]);
                setDivisions(d.data || []);
                setStats({ teams: t.data?.length || 0, articles: a.data?.length || 0 });
            } catch (e) {
                // ignore
            }
        })();
    }, []);

    return (
        <div data-testid="landing-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-t22252a" data-testid="hero-section">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-20"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="absolute inset-0 op-grid-bg opacity-40" />
                {/* radial glow under logo */}
                <div
                    className="absolute left-1/2 top-[18%] -translate-x-1/2 w-[700px] h-[700px] max-w-[90vw] max-h-[90vw] rounded-full pointer-events-none"
                    style={{
                        background: "radial-gradient(closest-side, rgba(43,182,255,0.25), rgba(255,122,26,0.10) 45%, transparent 70%)",
                        filter: "blur(20px)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-t090a0c/30 via-t090a0c/60 to-t090a0c" />

                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-24 flex flex-col items-center text-center">
                    <div className="op-label mb-6 op-fade-up" style={{ color: "rgb(var(--tc-ff7a1a))" }}>
                        EFOOTBALL · TOURNAMENT
                    </div>

                    {/* Big centered logo */}
                    <div
                        className="op-fade-up"
                        style={{ animationDelay: "60ms" }}
                    >
                        <img
                            src="/op-logo.png"
                            alt="Original PES"
                            className="w-64 sm:w-80 md:w-96 lg:w-[28rem] h-auto drop-shadow-[0_0_40px_rgba(43,182,255,0.4)]"
                        />
                    </div>

                    {/* STAGIONE 25 inline */}
                    <h1
                        className="op-heading text-tffffff mt-6 op-fade-up flex items-baseline justify-center gap-3 sm:gap-5"
                        style={{ animationDelay: "120ms" }}
                    >
                        <span className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl">STAGIONE</span>
                        <span
                            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
                            style={{
                                background: "linear-gradient(135deg, rgb(var(--tc-2bb6ff)) 0%, rgb(var(--tc-ff7a1a)) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}
                        >25</span>
                    </h1>

                    <p
                        className="max-w-2xl mt-6 text-tc9ccd3 text-base sm:text-lg leading-relaxed op-fade-up"
                        style={{ animationDelay: "180ms" }}
                    >
                        Non è il buono contro il cattivo. È che vinca il migliore in campo —
                        indipendentemente dalla storia, dal prestigio, dal budget. 56 squadre. 4 divisioni.
                        Un solo regolamento.
                    </p>

                    <div
                        className="flex flex-wrap justify-center gap-3 mt-10 op-fade-up"
                        style={{ animationDelay: "240ms" }}
                    >
                        <Link to="/regolamento" className="op-btn" data-testid="hero-rules-btn">
                            Leggi il regolamento
                        </Link>
                    </div>

                    {/* stat strip */}
                    <div
                        className="mt-16 w-full grid grid-cols-2 sm:grid-cols-4 border border-t22252a divide-x divide-t22252a op-fade-up"
                        style={{ animationDelay: "300ms" }}
                    >
                        <Stat label="Divisioni" value={divisions.filter((d) => d.code !== "PRIMAVERA" && d.competition_type !== "primavera").length || 4} />
                        <Stat label="Squadre" value={stats.teams} max={56} />
                        <Stat label="Articoli regolamento" value={stats.articles} />
                        <Stat label="Stagione" value="25" />
                    </div>
                </div>
            </section>

            {/* RECENT RESULTS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20" data-testid="recent-section">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// ULTIMI RISULTATI</div>
                        <h2 className="op-heading text-4xl sm:text-5xl mt-3">
                            <span className="op-gradient-text">DAL CAMPO</span>
                        </h2>
                    </div>
                    <p className="text-t8f95a3 max-w-md text-sm leading-relaxed">
                        Lega e coppe, in tempo reale. I risultati più recenti del torneo con marcatori e MVP.
                    </p>
                </div>
                <RecentResults limit={8} />
            </section>

            {/* GLOBAL LEADERBOARDS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="leaderboards-section">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="op-label" style={{ color: "rgb(var(--tc-2bb6ff))" }}>// CLASSIFICHE GLOBALI</div>
                        <h2 className="op-heading text-4xl sm:text-5xl mt-3">
                            <span className="op-gradient-text">I MIGLIORI DELLA STAGIONE</span>
                        </h2>
                    </div>
                    <p className="text-t8f95a3 max-w-md text-sm leading-relaxed">
                        Cannonieri, Pallone d&apos;Oro e Trofeo Yashin: i numeri di tutta la stagione,
                        sommando ogni campionato e ogni coppa.
                    </p>
                </div>
                <GlobalLeaderboards limit={8} />
            </section>

            {/* BACHECA TROFEI — vincitori da risultato (coppe/supercoppe + campioni di lega) */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="trophy-news-section">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="op-label" style={{ color: "rgb(var(--tc-ffd700))" }}>// ALBO IN TEMPO REALE</div>
                        <h2 className="op-heading text-4xl sm:text-5xl mt-3">
                            <span className="op-gradient-text">BACHECA TROFEI</span>
                        </h2>
                    </div>
                    <p className="text-t8f95a3 max-w-md text-sm leading-relaxed">
                        I campioni della stagione, appena alzano la coppa. Finali di coppa e supercoppa
                        col tabellino, e i campioni di lega quando il titolo è matematico.
                    </p>
                </div>
                <TrophyNews limit={12} />
            </section>

            {/* MERCATO OP — articoli AI sugli affari clamorosi */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="market-news-section">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="op-label" style={{ color: "rgb(var(--tc-ff4d4d))" }}>// MERCATO ORIGINAL PES</div>
                        <h2 className="op-heading text-4xl sm:text-5xl mt-3">
                            <span className="op-gradient-text">COLPI &amp; NOTIZIE BOMBA</span>
                        </h2>
                    </div>
                    <p className="text-t8f95a3 max-w-md text-sm leading-relaxed">
                        Gli affari più clamorosi della lega, raccontati in tempo reale.
                        Solo i colpi che contano: VG stellari, cifre monstre e scambi pesanti.
                    </p>
                </div>
                <MarketNews limit={10} />
            </section>

            {/* CALCIOMERCATO & NEWS (RSS pubblici) */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="news-section">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// CALCIOMERCATO &amp; NEWS</div>
                        <h2 className="op-heading text-4xl sm:text-5xl mt-3">
                            <span className="op-gradient-text">DAL MONDO DEL CALCIO</span>
                        </h2>
                    </div>
                    <p className="text-t8f95a3 max-w-md text-sm leading-relaxed">
                        Trattative, colpi di mercato e prestazioni in tempo reale, aggregati dalle
                        principali testate sportive italiane.
                    </p>
                </div>
                <FootballNews limit={9} />
            </section>

            {/* ANNOUNCEMENTS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="announcements-section">
                <Announcements limit={4} />
            </section>

            {/* IL MONDO DI OP — featured links */}
            <FeaturedLinks />

            {/* DIVISIONS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="divisions-section">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <h2 className="op-heading text-5xl mt-3">LE QUATTRO DIVISIONI</h2>
                    </div>
                    <p className="text-t8f95a3 max-w-md text-sm leading-relaxed">
                        Dalla TOP all&apos;HOPE, ogni livello racconta una storia diversa. Promozioni,
                        retrocessioni e coppe europee in palio ogni stagione.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(() => {
                        const cards = divisions.length
                            ? divisions.filter((d) => d.code !== "PRIMAVERA" && d.competition_type !== "primavera")
                            : [
                                  { code: "TOP", name: "TOP Division", accent_color: "rgb(var(--tc-ffd700))", description: "Massima serie" },
                                  { code: "DOP", name: "DOP Division", accent_color: "rgb(var(--tc-e0e0e0))", description: "Alta competitività" },
                                  { code: "OPS", name: "OPS Division", accent_color: "rgb(var(--tc-ff6b00))", description: "Cuore del progetto" },
                                  { code: "HOPE", name: "HOPE Division", accent_color: "rgb(var(--tc-00e5ff))", description: "Punto di partenza" },
                              ];
                        return cards.map((d, i) => (
                            <Link
                                key={d.code}
                                to={`/campionati/${d.code}`}
                                className="op-card group block p-0 overflow-hidden hover:border-tffffff transition-colors duration-150"
                                style={{ animationDelay: `${i * 60}ms` }}
                                data-testid={`division-card-${d.code.toLowerCase()}`}
                            >
                                <div className="aspect-[4/3] relative bg-t0d0f12 overflow-hidden">
                                    {DIVISION_IMAGES[d.code] && (
                                        <img
                                            src={DIVISION_IMAGES[d.code]}
                                            alt={d.code}
                                            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500"
                                        />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-t090a0c via-transparent to-transparent" />
                                </div>
                                <div className="p-5 border-t border-t22252a">
                                    <div className="op-heading text-2xl">{d.name}</div>
                                    <div className="text-t8f95a3 text-sm mt-2 leading-relaxed">
                                        {d.description || ""}
                                    </div>
                                </div>
                            </Link>
                        ));
                    })()}
                </div>
            </section>

            {/* MANIFESTO */}
            <section
                className="border-y border-t22252a bg-t0c0e11 py-24 px-4 sm:px-6 lg:px-8"
                data-testid="manifesto-section"
            >
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// MANIFESTO</div>
                        <h2 className="op-heading text-5xl sm:text-6xl mt-4 leading-[0.95]">
                            COSTRUIRE
                            <br />
                            UNA ROSA,
                            <br />
                            UN PROGETTO,
                            <br />
                            <span style={{
                                background: "linear-gradient(90deg, rgb(var(--tc-2bb6ff)) 0%, rgb(var(--tc-ff7a1a)) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}>UNA CARRIERA.</span>
                        </h2>
                    </div>
                    <div className="space-y-6 text-tc9ccd3 text-lg leading-relaxed">
                        <p>
                            Original PES non è una raccolta di partite online. È una simulazione
                            pensata per ricreare il calcio nella sua forma più autentica. Passaggi
                            manuali, tiri manuali, regole rigorose e un&apos;economia che pesa.
                        </p>
                        <p>
                            Ogni decisione conta: il monte stipendi, la formula salariale a 5
                            fattori, gli infortuni basati sulla resistenza PES, i tag su Facebook
                            per gli accordi, le sanzioni progressive. Tutto orchestrato dal{" "}
                            <span className="text-tffffff font-semibold">Consiglio dei Presidenti</span>.
                        </p>
                        <p className="op-mono text-sm text-t8f95a3">
                            56 club · 4 nazioni · 3 coppe europee · 1 regolamento ufficiale.
                        </p>
                    </div>
                </div>
            </section>

            {/* INTEGRATIONS TEASER */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="integrations-section">
                <div className="op-label text-t2bb6ff">// COMING NEXT</div>
                <h2 className="op-heading text-4xl sm:text-5xl mt-3 mb-10">
                    SINCRONIZZATI CON IL MONDO REALE
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                    <IntegrationCard
                        title="Transfermarkt"
                        text="Valori di mercato e foto giocatori importati direttamente dalla fonte ufficiale dei trasferimenti."
                    />
                    <IntegrationCard
                        title="PES DB · eFootball 26"
                        text="Statistiche autentiche, resistenza infortuni e ID giocatori sincronizzati dal database ufficiale del gioco."
                    />
                </div>
            </section>
        </div>
    );
}

function Stat({ label, value, max }) {
    return (
        <div className="p-5 sm:p-7">
            <div className="op-label">{label}</div>
            <div className="mt-2 op-mono text-3xl sm:text-4xl text-tffffff font-bold">
                {value}
                {max ? <span className="text-t5c6370 text-lg"> / {max}</span> : null}
            </div>
        </div>
    );
}

function IntegrationCard({ title, text, tag }) {
    return (
        <div className="op-card p-6">
            <div className="flex items-start justify-between">
                <div className="op-heading text-2xl">{title}</div>
                {tag && (
                    <span className="op-label text-t2bb6ff border border-t2bb6ff/40 px-2 py-1">
                        {tag}
                    </span>
                )}
            </div>
            <p className="mt-3 text-t8f95a3 text-sm leading-relaxed">{text}</p>
        </div>
    );
}
